import type { ReactNode } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import { formatDate } from '@/shared/format';
import { CapabilityList } from '@/modules/ai/shared';
import type { ProviderSummary, ProviderTestResult } from '@/api/schemas/ai';

export type ProviderOverviewProps = {
  provider: ProviderSummary;
  /** The result of a test run in this visit, if there has been one. */
  testResult: ProviderTestResult | null;
  isTesting: boolean;
  error: Error | null;
  onTest: () => void;
};

/** What the provider is, what it can do, and how it last answered. */
export function ProviderOverview(props: ProviderOverviewProps): ReactNode {
  return (
    <div className="space-y-6">
      {props.error ? <ErrorState title="Action failed" message={props.error.message} /> : null}

      <ConnectionSection provider={props.provider} />

      <section className="space-y-4 rounded-lg border border-border p-6">
        <h3 className="text-lg font-medium">Capabilities</h3>
        <CapabilityList capabilities={props.provider.capabilities} />
      </section>

      <TestSection
        provider={props.provider}
        result={props.testResult}
        isTesting={props.isTesting}
        onTest={props.onTest}
      />
    </div>
  );
}

function ConnectionSection({ provider }: { provider: ProviderSummary }): ReactNode {
  return (
    <section className="space-y-4 rounded-lg border border-border p-6">
      <h3 className="text-lg font-medium">Connection</h3>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <DetailField label="Base URL" value={provider.baseUrl} />
        <DetailField label="Model" value={provider.modelId} />
        <DetailField label="Timeout" value={`${provider.timeoutMs} ms`} />
        <DetailField
          label="Max output tokens"
          value={
            provider.maxOutputTokens === null ? 'Provider default' : String(provider.maxOutputTokens)
          }
        />
        <DetailField
          label="Temperature"
          value={provider.temperature === null ? 'Provider default' : String(provider.temperature)}
        />
        <DetailField label="Owner" value={provider.ownerScope} />
        <DetailField label="Version" value={String(provider.version)} />
        <DetailField label="Updated" value={formatDate(provider.updatedAt)} />
        <DetailField label="State" value={provider.state} />
      </dl>
    </section>
  );
}

type TestSectionProps = {
  provider: ProviderSummary;
  result: ProviderTestResult | null;
  isTesting: boolean;
  onTest: () => void;
};

function TestSection(props: TestSectionProps): ReactNode {
  const state = props.result?.state ?? props.provider.lastTestState;

  return (
    <section className="space-y-3 rounded-lg border border-border p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-medium">Connection test</h3>
        <Button variant="secondary" loading={props.isTesting} onClick={props.onTest}>
          Test connection
        </Button>
      </div>

      {state === null ? (
        <p className="text-sm text-content-muted">This provider has not been tested yet.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={state === 'ok' ? 'positive' : 'critical'}>
            {state === 'ok' ? 'Passed' : 'Failed'}
          </Badge>
          {props.result?.detail ? (
            <span className="text-sm text-content-muted">{props.result.detail}</span>
          ) : null}
          {!props.result && props.provider.lastTestedAt ? (
            <span className="text-sm text-content-muted">
              Last tested {formatDate(props.provider.lastTestedAt)}
            </span>
          ) : null}
        </div>
      )}
    </section>
  );
}

function DetailField({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-content-muted uppercase">{label}</dt>
      <dd className="text-sm text-content">{value}</dd>
    </div>
  );
}
